/* global $, APP, config */

import Logger from 'meet-hour-logger';
import React from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';

import { getActiveDOMDocument } from '../../../react/features/base/config/functions.any';
import { i18next } from '../../../react/features/base/i18n';
import { MEDIA_TYPE } from '../../../react/features/base/media';
import {
    getParticipantById,
    participantVolumeChanged
} from '../../../react/features/base/participants';
import { isTestModeEnabled } from '../../../react/features/base/testing';
import { getTrackByMediaTypeAndParticipant, updateLastTrackVideoMediaEvent } from '../../../react/features/base/tracks';
import { Thumbnail, isVideoPlayable } from '../../../react/features/filmstrip';
import { RemoteVideoContextMenu, showRemoteVideoContextMenu } from '../../../react/features/filmstrip/components/web/RemoteVideoContextMenu';
import { LAYOUTS } from '../../../react/features/video-layout';
import UIUtils from '../util/UIUtil';

import SmallVideo from './SmallVideo';

const logger = Logger.getLogger(__filename);

// const logger = {
//     ...console,
//     debug: console.log.bind(console, '[ScreenShareVideo]')
// }

/**
 * List of container events that we are going to process, will be added as listener to the
 * container for every event in the list. The latest event will be stored in redux.
 */
const containerEvents = [
    'abort',
    'canplay',
    'canplaythrough',
    'emptied',
    'ended',
    'error',
    'loadeddata',
    'loadedmetadata',
    'loadstart',
    'pause',
    'play',
    'playing',
    'ratechange',
    'stalled',
    'suspend',
    'waiting'
];

/**
 * Determines whether a video element needs a fresh track attachment.
 *
 * @param {HTMLVideoElement} videoElement - The video element to check.
 * @param {MHTrack} videoTrack - The track expected to be attached.
 * @returns {boolean}
 */
function needsVideoElementReattach(videoElement, videoTrack) {
    if (!videoElement || !videoTrack) {
        return false;
    }

    const { srcObject } = videoElement;

    if (!srcObject) {
        return true;
    }

    const currentTracks
        = typeof srcObject.getVideoTracks === 'function'
            ? srcObject.getVideoTracks()
            : [];

    if (!currentTracks.length) {
        return true;
    }

    if (currentTracks.some(track => track?.readyState === 'ended')) {
        return true;
    }

    const expectedStreamId = videoTrack.stream?.id;

    return Boolean(expectedStreamId && srcObject.id !== expectedStreamId);
}

/**
 *
 * @param {*} spanId
 */
function createContainer(spanId) {
    const container = document.createElement('span');

    container.id = spanId;
    container.className = 'videocontainer';

    const state = APP.store.getState();
    const activeWindow = getActiveDOMDocument(state);

    const remoteVideosContainer = activeWindow?.getElementById(
        'filmstripRemoteVideosContainer'
    );
    const localVideoContainer = activeWindow?.getElementById(
        'localVideoTileViewContainer'
    );

    if (remoteVideosContainer && localVideoContainer) {
        remoteVideosContainer.insertBefore(container, localVideoContainer);
    } else {
        remoteVideosContainer?.appendChild(container);
    }

    return container;
}

/**
 *
 */
export default class ScreenShareVideo extends SmallVideo {
    /**
     * Handles volume change from VolumeSlider and synchronizes everywhere.
     * @param {number} newVolume
     */
    onVolumeChange = newVolume => {
        // Synchronize volume for this participant
        if (typeof participantVolumeChanged === 'function') {
            APP.store.dispatch(participantVolumeChanged(this.id, newVolume));
        }

        // Optionally, update Redux or local state if needed
    };

    /**
   * Creates new instance of the <tt>ScreenShareVideo</tt>.
   *
   * @param user - {MHParticipant} The user for whom remote video instance will
   * be created.
   * @class
   */
    constructor(user, participant) {
        super();

        this.user = user;
        this.isFakeScreenshare = participant.isFakeScreenshare;
        this.id = participant.id;
        this.videoSpanId = this.isFakeScreenshare ? `participant_${this.isFakeScreenshare}_${this.id}` : `participant_${this.id}`;
        this.reactRoot = null;
        this._disposed = false;
        this.addRemoteVideoContainer();

        // After container is created, force a re-render to ensure video element is created if track is present
        setTimeout(() => this.renderThumbnail(), 0);
        this.bindHoverHandler();
        this.flipX = false;
        this.isLocal = false;
        this.container.addEventListener('contextmenu', e => {
            showRemoteVideoContextMenu(e, this.videoSpanId);
        });

        /**
         * The flag is set to <tt>true</tt> after the 'canplay' event has been
         * triggered on the current video element. It goes back to <tt>false</tt>
         * when the stream is removed. It is used to determine whether the video
         * playback has ever started.
         *
         * @type {boolean}
         */
        this._canPlayEventReceived = false;

        this.container.onclick = this._onContainerClick;

        this.lastVideoTrackId = undefined;
        this._unsubscribe = APP.store.subscribe(() => {
            if (this._disposed) {
                return;
            }

            this._updateObjectFitStyle();
            this.updateZoomTransform();

            const state = APP.store.getState();
            const currentParticipant = getParticipantById(state, this.id);
            const tracksNow = state['features/base/tracks'];
            const trackNow = getTrackByMediaTypeAndParticipant(tracksNow, MEDIA_TYPE.SCREENSHARE, this.id);
            const currentTrack = trackNow && trackNow.mhTrack;
            const currentVideoTrack = currentTrack || (currentParticipant && currentParticipant.videoTrack);
            const currentTrackId = currentVideoTrack
                && (typeof currentVideoTrack.getId === 'function'
                    ? currentVideoTrack.getId()
                    : currentVideoTrack.id);
            const videoElement = this.container && this.container.querySelector('video');
            const hasVideoElementNow = Boolean(videoElement);
            const expectedElementId = currentTrackId && `remoteVideo_${currentTrackId}`;
            const hasMismatchedElement = Boolean(videoElement && expectedElementId && videoElement.id !== expectedElementId);

            const hasDetachedElement = Boolean(
                videoElement
                    && (!videoElement.srcObject
                        || (typeof videoElement.srcObject.getVideoTracks === 'function'
                            && videoElement.srcObject.getVideoTracks().length === 0))
            );

            if (currentTrackId !== this.lastVideoTrackId
                || (currentTrack && (!hasVideoElementNow || hasMismatchedElement || hasDetachedElement))) {
                this.lastVideoTrackId = currentTrackId;
                this.renderThumbnail();
            }
        });

        // Ensure video element is created immediately if track is present
        this.renderThumbnail();
    }


    /**
     * Updates the objectFit style of the video element if it exists.
     */
    _updateObjectFitStyle() {
        // Find the video element for this participant
        const videoEl = this.container && this.container.querySelector('video');

        if (!videoEl) {
            return;
        }

        const state = APP.store.getState();
        const { filmStripView } = state['features/base/conference'];

        videoEl.style.objectFit = filmStripView === LAYOUTS.TILE_VIEW ? 'contain' : 'cover';
    }

    /**
   *
   */
    addRemoteVideoContainer() {
        this.container = createContainer(this.videoSpanId);
        this.$container = $(this.container);
        this.renderThumbnail();

        // this._setThumbnailSize();
        this.initBrowserSpecificProperties();

        return this.container;
    }

    /**
     * Renders the thumbnail.
     */
    renderThumbnail(isHovered = false) {
        // Do not (re)render after the instance has been removed. reactRoot.unmount()
        // flushes React synchronously and that commit can dispatch store actions
        // which re-enter this method via the store subscription; rendering then
        // would target a root that is being torn down and throw
        // "Cannot update an unmounted root".
        if (this._disposed || !this.container) {
            return;
        }

        if (!this.reactRoot) {
            this.reactRoot = createRoot(this.container); // Create root only once
        }

        // Get or create portal container for context menus
        let portalContainer = document.getElementById('context-menu-portal');

        if (!portalContainer) {
            portalContainer = document.createElement('div');
            portalContainer.id = 'context-menu-portal';
            document.body.appendChild(portalContainer);
        }

        this.reactRoot.render(
            <Provider store = { APP.store }>
                <I18nextProvider i18n = { i18next }>
                    <Thumbnail
                        isHovered = { isHovered }
                        participantID = { this.id } />
                    {createPortal(
                        <RemoteVideoContextMenu
                            participantId = { this.id }
                            showParticipantName = { true }
                            videoSpanId = { this.videoSpanId } />,
                        portalContainer
                    )}
                </I18nextProvider>
            </Provider>
        );

        // --- Ensure video element is present if video track is active ---
        const state = APP.store.getState();
        const participant = getParticipantById(state, this.id);
        const tracks = state['features/base/tracks'];

        // Debug: Log all screenshare tracks to understand what's available
        const screenshareTracks = tracks.filter(t => t.mediaType === 'screenshare');

        logger.log('[ScreenShareVideo] renderThumbnail - looking for track:', {
            participantId: this.id,
            isFakeParticipant: participant?.isFakeParticipant,
            allScreenshareTracks: screenshareTracks.map(t => {
                return {
                    participantId: t.participantId,
                    mediaType: t.mediaType,
                    videoType: t.videoType,
                    local: t.local,
                    muted: t.muted,
                    sourceName: t.mhTrack?.getSourceName?.()
                };
            })
        });

        const track = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, this.id);

        logger.log('[ScreenShareVideo] Track lookup result:', {
            participantId: this.id,
            trackFound: Boolean(track),
            track
        });

        if (track && track.mhTrack) {
            const videoTrack = track.mhTrack;
            const expectedElementId = `remoteVideo_${videoTrack.getId()}`;
            let videoElement = this.container.querySelector('video');

            if (videoElement && videoElement.id !== expectedElementId) {
                logger.info('[ScreenShareVideo] Replacing stale video element with new track', {
                    participantId: this.id,
                    currentElementId: videoElement.id,
                    expectedElementId
                });
                videoElement.remove();
                videoElement = null;
            }

            logger.log('[ScreenShareVideo] Track found, checking video element:', {
                participantId: this.id,
                hasVideoElement: Boolean(videoElement),
                hasAddRemoteStreamElement: typeof this.addRemoteStreamElement === 'function',
                videoElementDetails: videoElement ? {
                    videoId: videoElement?.id,
                    videoSrc: videoElement?.src,
                    videoSrcObject: videoElement?.srcObject,
                    hasStream: Boolean(videoElement?.srcObject),
                    streamId: videoElement?.srcObject?.id,
                    trackStreamId: videoTrack.stream?.id,
                    tracksInElement: videoElement?.srcObject?.getTracks?.()?.length
                } : null
            });

            if (videoElement) {
                const hasStream = Boolean(videoElement.srcObject);
                const streamMatches = videoElement.srcObject?.id === videoTrack.stream?.id;

                logger.log('[ScreenShareVideo] Video element stream status:', {
                    participantId: this.id,
                    hasStream,
                    streamMatches,
                    videoElementStreamId: videoElement.srcObject?.id,
                    trackStreamId: videoTrack.stream?.id
                });

                // If video element exists but stream is not attached or doesn't match, re-attach
                if (needsVideoElementReattach(videoElement, videoTrack)) {
                    logger.log('[ScreenShareVideo] Re-attaching stream to video element:', this.id);
                    try {
                        videoTrack.attach(videoElement);
                        logger.log('[ScreenShareVideo] Stream re-attached successfully:', this.id);
                    } catch (error) {
                        logger.error('[ScreenShareVideo] Error re-attaching stream:', error);
                        videoElement.remove();
                        this.addRemoteStreamElement(videoTrack);
                    }
                }
            } else if (typeof this.addRemoteStreamElement === 'function') {
                logger.log('[ScreenShareVideo] Adding remote stream element for:', this.id);
                this.addRemoteStreamElement(videoTrack);
            }
        } else {
            logger.warn('[ScreenShareVideo] No videoTrack for participant', {
                participantId: this.id,
                participant,
                trackCount: tracks.length,
                screenshareTrackCount: screenshareTracks.length
            });
        }
    }

    /**
   * Removes the remote stream element corresponding to the given stream and
   * parent container.
   *
   * @param stream - The MediaStream.
   * @param isVideo - <tt>true</tt> if given <tt>stream</tt> is a video one.
   */
    removeRemoteStreamElement(stream) {
        if (!this.container) {
            return false;
        }

        const isVideo = stream.isVideoTrack();
        const elementID = `remoteVideo_${stream.getId()}`;
        const select = $(`#${elementID}`);

        select.remove();
        if (isVideo) {
            this._canPlayEventReceived = false;
        }

        this.updateView();
    }

    /**
   * The remote video is considered "playable" once the can play event has been received.
   * For screenshare, we're more lenient - if the track exists and has a stream, consider it playable.
   *
   * @inheritdoc
   * @override
   */
    isVideoPlayable() {
        const state = APP.store.getState();
        const tracks = state['features/base/tracks'];
        const track = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, this.id);

        // For screenshare, if we have a track with a stream, consider it playable
        // even if canplay event hasn't fired yet
        const hasValidTrack = track && track.mhTrack && track.mhTrack.stream && !track.muted;

        return hasValidTrack || (
            isVideoPlayable(state, this.id)
            && this._canPlayEventReceived
        );
    }

    /**
   * @inheritDoc
   */
    updateView() {
        this.$container.toggleClass('audio-only', APP.conference.isAudioOnly());
        super.updateView();
    }

    /**
   * Removes ScreenShareVideo from the page.
   */
    remove() {
        this._disposed = true;

        // Unsubscribe from the store first. reactRoot.unmount() below flushes
        // React synchronously and that commit can dispatch actions which would
        // otherwise re-enter this subscriber and call renderThumbnail() on a
        // root that is in the middle of being unmounted.
        if (this._unsubscribe) {
            this._unsubscribe();
            this._unsubscribe = null;
        }

        if (this.reactRoot) {
            // Clear the reference before unmounting so that any re-entrant
            // renderThumbnail() call can never target this torn-down root.
            const root = this.reactRoot;

            this.reactRoot = null;
            root.unmount(); // Properly unmount before removing
        }

        // ReactDOM.unmountComponentAtNode(this.container);
        if (this.container) {
            this.container.innerHTML = '';
        }
        super.remove();
    }


    /**
   *
   * @param {*} streamElement
   * @param {*} stream
   */
    waitForPlayback(streamElement, stream) {
        $(streamElement).hide();

        const webRtcStream = stream.getOriginalStream();
        const isVideo = stream.isVideoTrack();

        if (!isVideo || webRtcStream.id === 'mixedmslabel') {
            return;
        }

        const listener = () => {
            this._canPlayEventReceived = true;

            if (streamElement) {
                $(streamElement).show();
            }

            streamElement.removeEventListener('canplay', listener);
            if (this._pendingCanPlay?.listener === listener) {
                this._pendingCanPlay = undefined;
            }

            // Refresh to show the video
            this.updateView();
            this.updateZoomTransform();
        };

        // The same <video> element is reused for every stream attached to this tile, so the listeners from the
        // previous attach have to be taken off it. Otherwise each re-attach (track replaced, unmute, reconnect)
        // left one more permanent loadedmetadata handler - and a canplay one if it had not fired yet - all
        // holding this thumbnail.
        if (this._pendingCanPlay) {
            this._pendingCanPlay.element.removeEventListener('canplay', this._pendingCanPlay.listener);
        }
        this._pendingCanPlay = {
            element: streamElement,
            listener
        };
        streamElement.addEventListener('canplay', listener);

        if (!this._onLoadedMetadata) {
            this._onLoadedMetadata = () => this.updateZoomTransform();
        }
        streamElement.removeEventListener('loadedmetadata', this._onLoadedMetadata);
        streamElement.addEventListener('loadedmetadata', this._onLoadedMetadata);
    }


    /**
   *
   * @param {*} stream
   */
    addRemoteStreamElement(stream) {

        if (!this.container) {
            logger.debug('[ScreenShareVideo] Not attaching remote stream due to no container', this.id, stream);

            return;
        }

        const isVideo = stream.isVideoTrack();

        logger.debug('[ScreenShareVideo] addRemoteStreamElement stream:', this.id, stream, this);
        logger.debug('[ScreenShareVideo] addRemoteStreamElement getOriginalStream:', this.id, stream.getOriginalStream && stream.getOriginalStream());

        if (!stream.getOriginalStream()) {
            logger.debug('[ScreenShareVideo] Remote video stream has no original stream', this.id, stream);

            return;
        }

        const expectedElementId = `remoteVideo_${stream.getId()}`;
        const existingVideoElement = this.container.querySelector('video');

        // Keep existing element only if it already belongs to the same track.
        if (existingVideoElement && existingVideoElement.id === expectedElementId
            && !needsVideoElementReattach(existingVideoElement, stream)) {
            logger.debug('[ScreenShareVideo] Remote video element already exists for stream', this.id, stream.getId());

            return;
        }

        if (existingVideoElement) {
            existingVideoElement.id = expectedElementId;
            this.waitForPlayback(existingVideoElement, stream);

            try {
                stream.attach(existingVideoElement);
            } catch (error) {
                logger.warn('[ScreenShareVideo] Failed to attach stream to existing video element, recreating element', {
                    participantId: this.id,
                    error
                });
                existingVideoElement.remove();
            }

            if (this.container.querySelector(`#${expectedElementId}`) === existingVideoElement) {
                this.updateZoomTransform();

                return;
            }
        }


        logger.log('[ScreenShareVideo] Attaching remote stream element', this.id, stream.getId(), this);


        let streamElement = document.createElement('video');

        streamElement.autoplay = !config.testing?.noAutoPlayVideo;
        streamElement.id = `remoteVideo_${stream.getId()}`;
        streamElement.muted = true;
        streamElement.playsInline = true;

        // Put new stream element always in front
        streamElement = UIUtils.prependChild(this.container, streamElement);

        this.waitForPlayback(streamElement, stream);
        stream.attach(streamElement);
        this.updateZoomTransform();

        if (isVideo && isTestModeEnabled(APP.store.getState())) {
            const cb = name => dispatch(updateLastTrackVideoMediaEvent(stream, name));

            containerEvents.forEach(event => {
                streamElement.addEventListener(event, cb.bind(this, event));
            });
        }
    }
}
